import { CloseOutlined } from "@mui/icons-material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SwipeableDrawer,
} from "@mui/material";
import { toast } from "sonner";
import { UnstyledButton } from "Components/Shared/Buttons/UnstyledButton";
import {
  type CardExportDownloadFormat,
  useExportCardTransactionsMutation,
} from "Api/Mutations/CardTransactions/useExportCardTransactionsMutation";
import type { CardTransactionFilters } from "Components/CardTransactions/utils/cardTransactionFilterUtils";
import { Resources, useResource } from "Translations/Resources";
import { TransactionExportFormat } from "Infrastructure/Api/Api";

const ExportResources = Resources.Components.CardTransactions.Export;

type DownloadOption = {
  format: CardExportDownloadFormat;
  label: string;
};

type Props = {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  filters: CardTransactionFilters;
};

export const ExportCardTransactionsDrawer = ({
  isOpen,
  onOpen,
  onClose,
  filters,
}: Props) => {
  const { t } = useResource();
  const { mutate: exportCardTransactions } =
    useExportCardTransactionsMutation();

  const downloadOptions: Array<DownloadOption> = [
    {
      format: TransactionExportFormat.Pdf,
      label: t(ExportResources.ExportPdf),
    },
    {
      format: TransactionExportFormat.Xlsx,
      label: t(ExportResources.ExportXlsx),
    },
    {
      format: TransactionExportFormat.Csv,
      label: t(ExportResources.ExportCsv),
    },
  ];

  const handleDownload = (format: CardExportDownloadFormat) => {
    exportCardTransactions(
      { format, filters },
      { onError: () => toast.error(t(ExportResources.DownloadError)) },
    );
    onClose();
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={isOpen}
      onClose={onClose}
      onOpen={onOpen}
      slotProps={{
        paper: {
          sx: {
            p: 3,
            maxHeight: "85vh",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          },
        },
      }}
      disableDiscovery
    >
      <Box display="flex" justifyContent="center" mb={6}>
        <Box
          aria-hidden
          sx={{ width: 80, height: 6, bgcolor: "grey.300", borderRadius: 2 }}
        />
        <UnstyledButton
          aria-label={t(Resources.Common.Back)}
          onClick={onClose}
          sx={{
            position: "absolute",
            color: "grey.600",
            right: 12,
            top: 12,
          }}
        >
          <CloseOutlined />
        </UnstyledButton>
      </Box>
      <List disablePadding sx={{ width: "100%", maxWidth: 600, mx: "auto" }}>
        {downloadOptions.map(({ format, label }) => (
          <ListItemButton key={label} onClick={() => handleDownload(format)}>
            <ListItemIcon>
              <FileDownloadIcon />
            </ListItemIcon>
            <ListItemText primary={label} />
          </ListItemButton>
        ))}
      </List>
    </SwipeableDrawer>
  );
};
